import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

import styles from "./index.module.scss";
import { cva, VariantProps } from "class-variance-authority";
import { InlineMarkdown, paragraphAlignClass } from "../Typography";

interface Props {
	copy: any;
	color: any[any];
	className?: string;
	layout: any;
	column?: string;
	card?: string;
	mcolor?: any;
	altColor?: any;
}

export function FeaturedParagraph({ copy, color, layout }: Props) {
	const style = { "--color": color?.hex } as React.CSSProperties;

	return (
		<>
			<p
				style={style as React.CSSProperties}
				className={` ${
					styles.paragraph
				} brandParagraphFeatured text-base lg:text-lg flex flex-col gap-4 ${
					copy?.length > 500 && layout !== "half" ? "lg:columns-2 gap-12" : ""
				}`}
			>
				<InlineMarkdown>{copy}</InlineMarkdown>
			</p>
		</>
	);
}

export function FeaturedWarning({ copy, color, layout }: Props) {
	const style = { "--color": color?.hex } as React.CSSProperties;

	return (
		<>
			<p
				style={style as React.CSSProperties}
				className={` ${styles.paragraph} FeaturedWarning uppercase font-extrabold text-center flex flex-col gap-4`}
			>
				<InlineMarkdown>{copy}</InlineMarkdown>
			</p>
		</>
	);
}

export function Paragraph({
	copy,
	color,
	layout,
	card,
	className,
	mcolor,
	altColor,
}: Props) {
	const style = {
		"--color": color?.hex,
		"--altColor": altColor?.hex,
	} as React.CSSProperties;
	return (
		<div
			style={style as React.CSSProperties}
			className={`  ${
				mcolor?.hex ? `${styles.paragraphVariant} ` : `${styles.paragraph}`
			}  text-sm lg:text-base flex flex-col   ${
				paragraphAlignClass(layout, card)
			} ${className}`}
		>
			<ReactMarkdown
				rehypePlugins={[rehypeRaw]}
				components={{
					p: ({ node, ...props }) => <p {...props} />,
					a: ({ node, ...props }) => (
						<a target="_blank" rel="noopener noreferrer" {...props} />
					),
					ul: ({ node, ...props }) => (
						<ul className="flex flex-col gap-2 lg:gap-4" {...props} />
					),
					h3: ({ node, ...props }) => (
						<p
							className={`${styles.altColor} text-base lg:text-lg brandTitle leading-tight `}
							{...props}
						/>
					),
					h1: ({ node, ...props }) => (
						<p
							className={`${styles.altColor} text-base lg:text-lg brandTitle leading-tight `}
							{...props}
						/>
					),
					div: React.Fragment,
				}}
			>
				{copy}
			</ReactMarkdown>
		</div>
	);
}

export function ParagraphNumber({
	copy,
	color,
	layout,
	card,
	className,
}: Props) {
	const style = { "--color": color?.hex } as React.CSSProperties;

	return (
		<p
			style={style as React.CSSProperties}
			className={`  ${
				styles.paragraph
			}  text-sm lg:text-base flex flex-col gap-4  brandParagraph ${
				paragraphAlignClass(layout, card)
			} ${className}`}
		>
			{copy}
		</p>
	);
}

export function Label({ copy, color, className }: Props) {
	const style = { "--color": color?.hex } as React.CSSProperties;
	return (
		<>
			<p
				style={style as React.CSSProperties}
				className={` ${styles.paragraph} ${className} brandLabel text-brandLabel lg:text-brandLabel_lg xl:text-brandLabel_xl max-w-full`}
			>
				<InlineMarkdown>{copy}</InlineMarkdown>
			</p>
		</>
	);
}

export function LabelNumber({ copy, color, className }: Props) {
	const style = { "--color": color?.hex } as React.CSSProperties;
	return (
		<>
			<p
				style={style}
				className={` ${styles.paragraph} ${className} text-xs max-w-full  font-bold font-secondary opacity-50`}
			>
				{copy}
			</p>
		</>
	);
}

export function FeaturedLabel({ copy, color, className }: Props) {
	const style = { "--color": color?.hex } as React.CSSProperties;
	return (
		<>
			<p
				style={style as React.CSSProperties}
				className={` ${styles.paragraph} ${className} brandFeaturedCall text-md lg:text-lg max-w-full`}
			>
				<InlineMarkdown>{copy}</InlineMarkdown>
			</p>
		</>
	);
}

export function HeroLabel({ copy, color, className, layout }: Props) {
	const style = { "--color": color?.hex } as React.CSSProperties;
	return (
		<>
			<p
				style={style as React.CSSProperties}
				className={`${layout == "center" ? "text-center" : "text-left"} ${
					styles.paragraph
				} ${className}   brandHeroLabel text-heroLabelSm lg:text-heroLabel max-w-full  `}
			>
				<InlineMarkdown>{copy}</InlineMarkdown>
			</p>
		</>
	);
}

export function ImageLabel({ copy, color, className }: Props) {
	const style = { "--color": color?.hex } as React.CSSProperties;
	return (
		<>
			<p
				style={style as React.CSSProperties}
				className={` ${styles.paragraph} ${className} brandImageLabel text-xs max-w-full pt-1 flex flex-col gap-2 `}
			>
				<ReactMarkdown
					rehypePlugins={[rehypeRaw]}
					components={{
						p: ({ node, ...props }) => <span {...props} />,
						div: React.Fragment,
						a: ({ node, ...props }) => (
							<a
								className="underline font-bold"
								target="_blank"
								rel="noopener noreferrer"
								{...props}
							/>
						),
					}}
				>
					{copy}
				</ReactMarkdown>
			</p>
		</>
	);
}

export function BigNumber({ copy, color, className, layout }: Props) {
	const style = { "--color": color?.hex } as React.CSSProperties;
	return (
		<p
			style={style as React.CSSProperties}
			className={` ${styles.paragraph} ${className} text-center   text-heroLabelSm lg:text-heroLabel max-w-full  brandTitle `}
		>
			<InlineMarkdown>{copy}</InlineMarkdown>
		</p>
	);
}

// 2025 DEV

export type ParaVariantProps = VariantProps<typeof ParaVariants>;
const ParaVariants = cva(`flex flex-col  `, {
	variants: {
		type: {
			image: "",
			label: "",
			showcaseLabel: "",
			showcaseProductName: "",
			limitedEditionCard: "",
			limitedEditionInfoTitle: "",
			default: "",
			quote: "",
			excerpt: "",
			featuredText: "",
			headline: "",
			featured: "",
			quoteSignature: "",
			subLabel: "",
			headlineFeatured: "",
			/** Portfolio zigzag overwrite: theme `.zigzagCopy` (ROMEIRA lg+ Desktop/P; other brands = headline) */
			zigzagCopy: "",
		},
		font: {
			sans: "font-sans",
			serif: "font-secondary",
		},
		weight: {
			default: "font-normal",
			bold: "font-bold",
		},
		transform: {
			uppercase: "uppercase",
			lowercase: "lowercase",
			capitalize: "capitalize",
		},
		align: {
			center: "text-center",
			left: "text-left",
			right: "text-right",
		},
		clamp: {
			true: "line-clamp-3",
			false: "",
		},
	},
	compoundVariants: [
		{
			type: "default",
			class: "brandParagraph gap-4",
		},
		{
			type: "image",
			class: "imageText",
		},
		{
			type: "label",
			class: "labelText",
		},
		{
			type: "showcaseLabel",
			class: "labelTextShowcase",
		},
		{
			type: "showcaseProductName",
			class: "productNameShowcase",
		},
		{
			type: "limitedEditionCard",
			class: "limitedEditionCardTitle",
		},
		{
			type: "limitedEditionInfoTitle",
			class: "limitedEditionInfoTitle",
		},
		{
			type: "subLabel",
			class: "subLabelText uppercase",
		},
		{
			type: "featured",
			class: "multiFeatured",
		},
		{
			type: "quoteSignature",
			class: "quoteSignature",
		},
		{
			type: "headline",
			class: "headlineText",
		},
		{
			type: "featuredText",
			class: "featuredText",
		},
		{
			type: "excerpt",
			class: "excerptText",
		},
		{
			type: "quote",
			class: "quoteText",
		},
		{
			type: "headlineFeatured",
			class: "headlineFeaturedText",
		},
		{
			type: "zigzagCopy",
			class: "zigzagCopy",
		},
	],
});

export interface ParaProps
	extends React.HTMLAttributes<HTMLHeadingElement>,
		ParaVariantProps {
	data: any;
	color: any;
	titleColor?: any;
	mcolor?: any;
}

export const MultiPurposeParagraph = ({
	data,
	color,
	type,
	weight,
	font,
	align,
	titleColor,
	transform,
	mcolor,
}: ParaProps) => {
	const style = {
		"--color": color?.hex,
		"--titleColor": titleColor?.hex,
		"--mcolor": mcolor?.hex,
	} as React.CSSProperties;

	return (
		<>
			<div
				style={style as React.CSSProperties}
				className={`  ${
					mcolor?.hex
						? `${styles.paragraphVariant} `
						: `${styles.paragraph}
        `
				}  ${ParaVariants({
					type,
					weight,
					font,
					align,
					transform,
				})}`}
			>
				<ReactMarkdown
					rehypePlugins={[rehypeRaw]}
					components={{
						h3: ({ node, ...props }) => (
							<p
								style={{ textWrap: "balance" }}
								className={`multiHeading ${styles.multiTitle} `}
								{...props}
							/>
						),
						h2: ({ node, ...props }) => (
							<p
								style={{ textWrap: "balance" }}
								className={`multiHeading ${styles.multiTitle}`}
								{...props}
							/>
						),
						h1: ({ node, ...props }) => (
							<p
								style={{ textWrap: "balance" }}
								className={`multiHeading ${styles.multiTitle}`}
								{...props}
							/>
						),
						blockquote: ({ node, ...props }) => (
							<span className="subLabel mb-2 lg:mb-4 uppercase" {...props} />
						),
						p: ({ node, ...props }) => <p {...props} />,
						a: ({ node, ...props }) => (
							<a className="underline font-bold" {...props} />
						),

						ul: ({ node, ...props }) => (
							<ul className="flex flex-col gap-2 list-disc pl-8" {...props} />
						),
						div: React.Fragment,
					}}
				>
					{data}
				</ReactMarkdown>
			</div>
		</>
	);
};
